/**
 * section-product-customizer-2.js
 *
 * Behavior for the product.customizer-2 test template's two pop-ups:
 *  1. Color pop-up ("See All Colors") -- clicking a swatch swaps the large
 *     preview image only. Browsing only, no variant selection / add-to-cart.
 *  2. Zakeke customizer pop-up ("Start Customizing") -- lazy-loads Zakeke's
 *     real Shopify integration script the first time the modal opens.
 *
 * Both modals are the theme's existing <modal-dialog> web component
 * (assets/global.js, unmodified) -- this file only adds behavior on top of
 * the open/close it already provides via [data-modal-opener] and the
 * "open" class on .modal.
 *
 * Zakeke integration mechanism (confirmed against this store's own live
 * /apps/zakeke?pid=<id> response, not assumed):
 *  - There is no OAuth/token exchange and no separate JSON config endpoint.
 *    Zakeke's Shopify app renders the container, add-to-cart form, and its
 *    own <script> tags (culminating in a `zakekeDesigner({...})` call, with
 *    the per-product config already resolved server-side) directly into
 *    the HTML returned by /apps/zakeke?pid=<product id>. This is the same
 *    URL/pattern CLAUDE.md documents theme.liquid using for the full-page
 *    Zakeke flow.
 *  - That fragment lives inside a `.icartShopifyCartContent` wrapper. We
 *    fetch the page, scope everything to that wrapper, and:
 *      a. clone the #zakeke-addtocart form (a plain sibling element, not a
 *         script -- easy to miss, but Zakeke's iframe submits through it)
 *      b. clone any Zakeke <link> stylesheet (link tags apply automatically
 *         once connected to the document, no replay needed)
 *      c. recreate every <script> tag as a real element (scripts parsed via
 *         DOMParser are inert and never auto-execute -- they must be
 *         recreated to run)
 *  - Scoped to `.icartShopifyCartContent` specifically so we don't replay
 *    the rest of that fetched page's scripts (analytics, cart drawer init,
 *    etc.) a second time.
 *  - Loaded once per page view, on first modal open -- not torn down and
 *    reloaded on every open/close, since Zakeke's own script has no
 *    documented teardown API and re-calling zakekeDesigner() on the same
 *    container is unverified. If Zakeke needs a hard reset between opens,
 *    that needs Zakeke's own guidance.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * Color pop-up: preload every swatch's large image once, on first open
   * ------------------------------------------------------------------- */
  /* Clicking a swatch swaps the main image's src directly, which for a
     not-yet-requested image means the browser has to fetch it before
     anything paints -- confirmed live, a visible lag on the first click of
     each color. Preloading every swatch's large-image URL as soon as the
     modal opens means that fetch has already happened (or is in flight)
     by the time a color is actually clicked, so the swap reads from cache
     instead.

     Capped at 4 concurrent requests rather than firing all of them at
     once: with 236 colors on some products, flooding the connection queue
     with that many simultaneous requests makes the swap slower, not
     faster, since whichever image is actually clicked ends up stuck in
     line behind the rest. A small fixed concurrency keeps steady
     background progress without starving the next click. */
  var colorPreloadState = null; /* null | 'done' */
  function preloadColorPreviewImages(modal) {
    if (colorPreloadState === 'done') return;
    colorPreloadState = 'done';

    var sources = [];
    modal.querySelectorAll('[data-color-swatch-btn]').forEach(function (btn) {
      var src = btn.getAttribute('data-large-image');
      if (src) sources.push(src);
    });

    var CONCURRENCY = 4;
    var nextIndex = 0;

    function loadNext() {
      if (nextIndex >= sources.length) return;
      var src = sources[nextIndex++];
      var img = new Image();
      img.onload = img.onerror = loadNext;
      img.src = src;
    }

    for (var i = 0; i < CONCURRENCY; i++) {
      loadNext();
    }
  }

  /* ---------------------------------------------------------------------
   * Color pop-up: swap large image on swatch click
   * ------------------------------------------------------------------- */
  function initColorPopup() {
    var modal = document.getElementById('ColorPopup2Modal');
    if (!modal) return;

    var mainImage = modal.querySelector('[data-color-popup-main-image]');
    var mainLabel = modal.querySelector('[data-color-popup-main-label]');
    var swatchButtons = modal.querySelectorAll('[data-color-swatch-btn]');

    swatchButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var largeSrc = btn.getAttribute('data-large-image');
        if (!mainImage || !largeSrc) return;

        mainImage.setAttribute('src', largeSrc);
        mainImage.removeAttribute('srcset');
        mainImage.setAttribute('alt', btn.getAttribute('data-alt') || '');

        if (mainLabel) {
          mainLabel.textContent = btn.getAttribute('data-variant-title') || '';
        }

        swatchButtons.forEach(function (other) {
          other.classList.remove('is-active');
        });
        btn.classList.add('is-active');
      });
    });

    /* Shared by the category-button click handler and the default-filter
       call below -- one place decides which swatches a category shows and
       what happens to the preview if the active swatch gets filtered out.
       firstVisible.click() re-fires the click listener already bound
       above instead of duplicating its image/label/is-active logic. */
    function applyCategoryFilter(category) {
      var firstVisible = null;
      var activeStillVisible = false;

      swatchButtons.forEach(function (btn) {
        var matches =
          category === 'all' ||
          (category === 'most-popular' && btn.getAttribute('data-filter-popular') === 'true') ||
          btn.getAttribute('data-filter-category') === category;

        btn.classList.toggle('is-filtered-out', !matches);

        if (matches) {
          if (!firstVisible) firstVisible = btn;
          if (btn.classList.contains('is-active')) activeStillVisible = true;
        }
      });

      if (!activeStillVisible && firstVisible) {
        firstVisible.click();
      }
    }

    var categoryButtons = modal.querySelectorAll('[data-color-category-btn]');
    categoryButtons.forEach(function (catBtn) {
      catBtn.addEventListener('click', function () {
        categoryButtons.forEach(function (other) {
          var isSelected = other === catBtn;
          other.classList.toggle('is-active', isSelected);
          other.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
        applyCategoryFilter(catBtn.getAttribute('data-category') || 'all');
      });
    });

    var defaultCategoryBtn = modal.querySelector('[data-color-category-btn].is-active');
    if (defaultCategoryBtn) {
      applyCategoryFilter(defaultCategoryBtn.getAttribute('data-category') || 'all');
    }
  }

  /* ---------------------------------------------------------------------
   * Zakeke customizer pop-up: load the real Zakeke fragment on first open
   * ------------------------------------------------------------------- */
  var ZAKEKE_APP_PROXY_PATH = '/apps/zakeke';
  var zakekeLoadState = null; /* null | 'loading' | 'loaded' | 'error' */

  /* Zakeke's stylesheet (portal.zakeke.com/.../customizer.css) is built
     for its normal full-page usage and sets a global `body { overflow:
     hidden }` rule of its own -- confirmed live by disabling the cloned
     <link> and watching document.body's computed overflow flip from
     hidden back to visible. Since we clone this stylesheet into <head>
     once and never removed it, that rule stayed active (silently
     breaking page scroll) for the rest of the session after the first
     time the modal was opened, even once our own modal-close logic had
     already removed its own .scroll-fixed lock. Tracked here so it can be
     disabled specifically when the modal closes (Zakeke still needs its
     own styles while the modal is open, so it's re-enabled on reopen --
     not just deleted outright). */
  var zakekeStylesheetLinks = [];
  function setZakekeStylesheetEnabled(enabled) {
    zakekeStylesheetLinks.forEach(function (link) {
      link.disabled = !enabled;
    });
  }

  /* assets/component-cart-form.js's Edit Design link (cart line item ->
     /products/<handle>?view=customizer-2&design=...|composition=...
     &variant=...&quantity=...) lands here wanting the modal to open
     already pointed at the customer's existing design instead of a blank
     one. Read once into a module-scoped object rather than a DOM data
     attribute -- there's exactly one #zakeke-container on this template,
     so there's nothing per-instance to key the params by. */
  var zakekeEditParams = null;
  function readZakekeEditParamsFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var design = params.get('design');
    var composition = params.get('composition');
    return {
      design: design,
      composition: composition,
      variant: params.get('variant'),
      quantity: params.get('quantity'),
      hasEditParam: !!(design || composition)
    };
  }

  /* The customizer-2 template is reached via a plain product URL
     (/products/<handle>?view=customizer-2...), so the handle is always the
     first path segment -- no need for a Liquid-rendered data attribute
     just to recover it here. */
  function getProductHandleFromPath() {
    var match = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    return match ? match[1] : null;
  }

  /* Mirrors the cart's "Edit Design" configuration-based link (assets/
     component-cart-form.js), which uses Zakeke's handle+variant route
     (/apps/zakeke/c/<handle>) rather than the pid route -- relaying it
     through unchanged is what makes editing that branch's designs work
     the same way the design/pid branch already does. */
  function buildZakekeFetchUrl(productId) {
    var edit = zakekeEditParams || {};

    if (edit.composition) {
      var handle = getProductHandleFromPath();
      if (!handle) return null;
      var url = '/apps/zakeke/c/' + handle + '?composition=' + encodeURIComponent(edit.composition);
      if (edit.variant) url += '&variant=' + encodeURIComponent(edit.variant);
      return url;
    }

    if (!productId) return null;

    var pidUrl = ZAKEKE_APP_PROXY_PATH + '?pid=' + encodeURIComponent(productId);
    if (edit.design) pidUrl += '&design=' + encodeURIComponent(edit.design);
    if (edit.quantity) pidUrl += '&quantity=' + encodeURIComponent(edit.quantity);
    return pidUrl;
  }

  function openZakekeCustomizer() {
    var container = document.getElementById('zakeke-container');
    if (!container || zakekeLoadState) return;

    var productId = container.getAttribute('data-zakeke-product-id');
    var fetchUrl = buildZakekeFetchUrl(productId);
    if (!fetchUrl) {
      zakekeLoadState = 'error';
      container.innerHTML =
        '<p class="wrw-c2-zakeke-error">This product is not set up for customization yet.</p>';
      return;
    }

    zakekeLoadState = 'loading';

    fetch(fetchUrl)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Zakeke request failed with status ' + response.status);
        }
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var scope =
          doc.querySelector('.icartShopifyCartContent') ||
          doc.querySelector('#MainContent') ||
          doc.body;

        if (!scope.querySelector('#zakeke-container')) {
          /* Most likely cause: /apps/zakeke redirected (e.g. the NIVA gate
             theme.liquid applies) instead of returning the customizer. */
          throw new Error('Zakeke container missing from /apps/zakeke response');
        }

        /* Zakeke's own script appends its iframe into #zakeke-container --
           it never clears existing content first -- so the "Loading
           customizer..." placeholder was staying on screen permanently,
           sitting on top of the real customizer once it mounted. Confirmed
           live: container.innerHTML still had the placeholder <p> as its
           first child even after the fetch/script chain completed. */
        container.innerHTML = '';

        var sourceForm = scope.querySelector('#zakeke-addtocart');
        if (sourceForm && !document.getElementById('zakeke-addtocart')) {
          var clonedForm = sourceForm.cloneNode(true);
          if (clonedForm.getAttribute('action') === '/cart/add') {
            clonedForm.setAttribute('action', '/cart/add.js');
          }
          container.insertAdjacentElement('afterend', clonedForm);
        }

        scope.querySelectorAll('link[href*="zakeke"]').forEach(function (link) {
          var href = link.getAttribute('href');
          if (href && !document.querySelector('link[href="' + href + '"]')) {
            var cloned = link.cloneNode(true);
            document.head.appendChild(cloned);
            zakekeStylesheetLinks.push(cloned);
          }
        });

        scope.querySelectorAll('script').forEach(function (oldScript) {
          var newScript = document.createElement('script');
          for (var i = 0; i < oldScript.attributes.length; i++) {
            var attr = oldScript.attributes[i];
            newScript.setAttribute(attr.name, attr.value);
          }
          newScript.textContent = oldScript.textContent;
          document.body.appendChild(newScript);
        });

        zakekeLoadState = 'loaded';
      })
      .catch(function (err) {
        console.error('Zakeke customizer could not be initialized:', err);
        zakekeLoadState = 'error';
        container.innerHTML =
          '<p class="wrw-c2-zakeke-error">Sorry, the customizer could not be loaded. Please try again.</p>';
      });
  }

  function initZakekePopup() {
    var modalEl = document.getElementById('ZakekeCustomizer2Modal');
    if (!modalEl) return;

    /* querySelectorAll, not querySelector: the sticky mobile "Start
       Customizing" bar duplicates this trigger, so there can be more than
       one on the page. openZakekeCustomizer() is itself idempotent
       (guarded by zakekeLoadState), so wiring every match is safe even if
       more than one ends up firing it. */
    document.querySelectorAll('[data-zakeke-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', openZakekeCustomizer);
    });
  }

  /* ---------------------------------------------------------------------
   * Hide the on-site chat widget while either pop-up is open
   * ------------------------------------------------------------------- */
  /* Toggles a body class rather than hiding matched elements directly.
     Confirmed live: this store's chat bubble is a Klaviyo on-site teaser
     (a Shopify app embed, not theme code) that loads asynchronously and
     can inject itself into the page *after* a modal is already open
     (more likely on mobile, where it's often further delayed) -- a
     one-time direct-hide at open-time doesn't catch an element that
     doesn't exist yet. The matching CSS rule (in
     section-product-customizer-2-zakeke-popup.liquid, shared by both
     pop-ups) applies continuously to any current or future element, no
     timing dependency. See that file for why !important is required. */
  function setChatWidgetHidden(hidden) {
    document.body.classList.toggle('wrw-c2-hide-chat-widget', hidden);
  }

  /* Tracked separately per modal (not one shared boolean) so that closing
     one modal can't prematurely reveal the widget while the other is
     still open. */
  var chatHiddenBy = { zakeke: false, color: false };
  function recomputeChatVisibility() {
    setChatWidgetHidden(chatHiddenBy.zakeke || chatHiddenBy.color);
  }

  function watchModalOpenState(modalInner, onChange) {
    if (!modalInner) return;
    function sync() {
      onChange(modalInner.classList.contains('open'));
    }
    sync();
    if (window.MutationObserver) {
      var observer = new MutationObserver(sync);
      observer.observe(modalInner, { attributes: true, attributeFilter: ['class'] });
    }
  }

  /* Also triggers the color-modal image preload above -- both need the
     same "did this modal just open" signal, so they share one observer
     per modal instead of registering two. */
  function initModalOpenWatchers() {
    var zakekeModalEl = document.getElementById('ZakekeCustomizer2Modal');
    var zakekeInner = zakekeModalEl ? zakekeModalEl.querySelector('.modal') : null;
    watchModalOpenState(zakekeInner, function (isOpen) {
      /* Belt-and-suspenders alongside the click listeners in
         initZakekePopup(): matches WristCo's own dual-trigger pattern.
         openZakekeCustomizer() is idempotent, so this is a safety net,
         not a second load. */
      if (isOpen) {
        openZakekeCustomizer();
      }
      setZakekeStylesheetEnabled(isOpen);
      chatHiddenBy.zakeke = isOpen;
      recomputeChatVisibility();
    });

    var colorModalEl = document.getElementById('ColorPopup2Modal');
    var colorInner = colorModalEl ? colorModalEl.querySelector('.modal') : null;
    watchModalOpenState(colorInner, function (isOpen) {
      if (isOpen && colorModalEl) {
        preloadColorPreviewImages(colorModalEl);
      }
      chatHiddenBy.color = isOpen;
      recomputeChatVisibility();
    });
  }

  /* ---------------------------------------------------------------------
   * Sticky mobile "Start Customizing" bar
   * ------------------------------------------------------------------- */
  /* Mirrors this theme's own existing sticky-add-to-cart pattern
     (assets/template-product.js's stickyCheckout()) and WristCo's
     co-customizer2-hero.liquid: shows the duplicate fixed-position button
     once the real on-page button scrolls out of view, hides it again once
     scrolled back to it. */
  function initStickyStartButton() {
    var onPageBtn = document.querySelector('[data-c2-start-onpage]');
    var stickyBar = document.querySelector('[data-c2-sticky-start]');
    if (!onPageBtn || !stickyBar || !window.IntersectionObserver) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          stickyBar.classList.remove('active');
        } else {
          stickyBar.classList.add('active');
        }
      });
    });
    observer.observe(onPageBtn);
  }

  document.addEventListener('DOMContentLoaded', function () {
    zakekeEditParams = readZakekeEditParamsFromUrl();

    initColorPopup();
    initZakekePopup();
    initModalOpenWatchers();
    initStickyStartButton();

    /* Edit Design's whole point is landing already open, pre-loaded with
       the existing design -- same modal.show() the click handlers in
       initZakekePopup() already trigger via [data-zakeke-trigger], just
       invoked on page load instead of a click. initModalOpenWatchers()'s
       MutationObserver (registered just above) is what actually calls
       openZakekeCustomizer() once the "open" class lands, same as a real
       click would trigger -- so this doesn't need its own load call. */
    if (zakekeEditParams.hasEditParam) {
      var modalEl = document.getElementById('ZakekeCustomizer2Modal');
      if (modalEl && typeof modalEl.show === 'function') {
        modalEl.show();
      }
    }
  });
})();
